import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles, UIBox, UICircularProgress } from "../../_core/components";
import UITextInput from "../../_core/components/ui/UITextInput";
import UIModalDialog from "../../_core/components/ui/UIModalDialog";
import { useCreatePartnerUser } from "../hooks";
import { CreatePartnerUserParams } from "../api/types";
import { useApiAlerts } from "../../_core/hooks";
import { isEmail } from "../../_core/utils";
import { PersonAdd as PersonAddIcon } from "../../_core/components/icons";

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateUserModal = ({ open, onClose }: CreateUserModalProps) => {
  const classes = useStyles();
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<CreatePartnerUserParams>({
    firstName: "",
    lastName: "",
    email: "",
  });
  
  const [errors, setErrors] = useState<Partial<CreatePartnerUserParams>>({});

  const {
    mutate: createUser,
    isPending,
    isSuccess,
    error,
    isError,
    reset,
  } = useCreatePartnerUser();

  useApiAlerts(
    isSuccess ? "User created successfully" : undefined,
    error,
    isError
  );

  const handleClose = useCallback(() => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
    });
    setErrors({});
    reset(); // Reset the mutation state
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess, handleClose]);

  // Focus management for accessibility
  useEffect(() => {
    if (open && firstInputRef.current) {
      // Focus first input when modal opens
      const timer = setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100); // Small delay to ensure modal is fully rendered
      
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleInputChange = (field: keyof CreatePartnerUserParams) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };


  const handleInputBlur = (field: keyof CreatePartnerUserParams) => (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.trim();
    
    // Validate email on blur
    if (field === "email" && value && !isEmail(value)) {
      setErrors(prev => ({
        ...prev,
        email: "Please enter a valid email address"
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CreatePartnerUserParams> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isEmail(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      createUser(formData);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      // Ctrl+Enter or Cmd+Enter to submit
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <UIModalDialog
      open={open}
      onClose={handleClose}
      type="info"
      icon={<PersonAddIcon />}
      title="Create New User"
      subtitle="Add a new partner user to your organization"
      maxWidth="sm"
      fullWidth
      actions={[
        {
          label: "Cancel",
          onClick: handleClose,
          variant: "text",
          disabled: isPending
        },
        {
          label: isPending ? "Creating User..." : "Create User",
          onClick: handleSubmit,
          variant: "contained",
          icon: isPending ? <UICircularProgress size={18} color="inherit" /> : <PersonAddIcon />,
          disabled: isPending,
          loading: isPending
        }
      ]}
    >
      <UIBox className={classes.form} onKeyDown={handleKeyDown}>
        <UITextInput
          label="First Name"
          value={formData.firstName}
          onChange={handleInputChange("firstName")}
          error={!!errors.firstName}
          helperText={errors.firstName}
          fullWidth
          required
          inputRef={firstInputRef}
        />
        <UITextInput
          label="Last Name" 
          value={formData.lastName}
          onChange={handleInputChange("lastName")}
          error={!!errors.lastName}
          helperText={errors.lastName}
          fullWidth
          required
        />
        <UITextInput
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleInputChange("email")}
          onBlur={handleInputBlur("email")}
          error={!!errors.email}
          helperText={errors.email}
          fullWidth
          required
        />
      </UIBox>
    </UIModalDialog>
  );
};

const useStyles = makeStyles(() => ({
  form: {
    display: "flex",
    flexDirection: "column",
  },
}));

export default CreateUserModal;